import React from "react"
import ReactDOM from "react-dom/client"
import { ProductCard } from "@app/components/ProductCard"
import { EVENT_CART_UPDATE } from "@app/components/cart"
import { getCollectionProducts, getProduct } from "@app/lib/product"
import { Product } from "@app/types/product"
import { Z_CODE_PRODUCT_TAG } from "@app/utils/z-codes"

class ProductRecommendation extends HTMLElement {
  $carousel: Element | null = null;
  $prevButton: Element | null = null;
  $nextButton: Element | null = null;
  $scrollProgressWrapper: Element | null = null;
  $scrollProgress: HTMLDivElement | null = null;
  collectionProducts: Product[] = [];

  constructor() {
    super();
    this.$carousel = this.querySelector(".carousel-main");
    this.$prevButton = this.querySelector(".carousel-button-left");
    this.$nextButton = this.querySelector(".carousel-button-right");
    this.$scrollProgressWrapper = this.querySelector(".scroll-progress-wrapper");
    this.$scrollProgress = this.querySelector(".scroll-progress");
  }

  connectedCallback() {
    this.style.display = "none";

    // if `data-product-handle` is present fetch recommendations based on that,
    // otherwise fetch based on the products in the cart
    if (this.dataset.productHandle) {
      this.init([this.dataset.productHandle]);
    } else {
      document.addEventListener(EVENT_CART_UPDATE, (e: any) => {
        const { cart } = e.detail || {};
        if (cart) {
          let handles = cart.items.map((i: any) => i.handle);
          handles = [...new Set(handles)];
          this.init(handles);
        }
      });
    }
  }

  async init(handles: string[]) {
    await this.fetchRecommendedProducts(handles);
    if (this.collectionProducts.length > 0) {
      this.style.display = "block";
      setTimeout(() => {
        this.initCarousel();
      }, 1000);
    } else {
      this.style.display = "none";
    }
  }

  async fetchRecommendedProducts(fromHandles: string[]) {
    const fromProducts = await Promise.all(fromHandles.map((h) => getProduct(h)));
    let collectionIds = fromProducts.map((i) => i.collections?.edges?.[0]?.node?.id).filter((c) => c);
    collectionIds = [...new Set(collectionIds)];
    let collectionProducts: any = await Promise.all(collectionIds.map((id: any) => getCollectionProducts(id)));
    this.collectionProducts = collectionProducts.flat().filter((p: any) =>
      !fromHandles.includes(p.handle) && !p.tags?.includes(Z_CODE_PRODUCT_TAG)
    );
    this.initProductCards();
  }

  initProductCards() {
    this.$carousel!.innerHTML = "";

    this.collectionProducts.forEach((product) => {
      const $productCard = document.createElement("div");
      $productCard.classList.add("shrink-0", "snap-center");
      this.$carousel?.appendChild($productCard);
      const root = ReactDOM.createRoot($productCard);
      root.render(<ProductCard product={product} fixedWidth />);
    });
  }

  initCarousel() {
    const { scrollWidth = 0, clientWidth = 0 } = this.$carousel || {};
    if (scrollWidth === clientWidth) {
      this.$prevButton?.classList.add("!hidden");
      this.$nextButton?.classList.add("!hidden");
      this.$scrollProgressWrapper?.classList.add("!hidden");
      return;
    }

    this.initScrollProgress();

    this.$nextButton?.addEventListener("click", () => {
      this.$carousel?.scrollBy({
        left: this.$carousel?.clientWidth / 2,
        behavior: "smooth",
      });
      const { scrollLeft = 0, scrollWidth = 0, clientWidth = 0 } = this.$carousel || {};
      if (scrollLeft + clientWidth - scrollWidth > -1) {
        this.$carousel?.scrollTo({
          left: 0,
          behavior: "smooth",
        });
      }
    });

    this.$prevButton?.addEventListener("click", () => {
      this.$carousel?.scrollBy({
        left: -this.$carousel?.clientWidth / 2,
        behavior: "smooth",
      });
      const { scrollLeft = 0, scrollWidth = 0 } = this.$carousel || {};
      if (scrollLeft < 1) {
        this.$carousel?.scrollTo({
          left: scrollWidth,
          behavior: "smooth",
        });
      }
    });
  }

  initScrollProgress() {
    this.updateScrollProgress();
    this.$carousel?.addEventListener(
      "scroll",
      () => {
        this.updateScrollProgress();
      },
      { passive: true },
    );
  }

  updateScrollProgress() {
    if (!this.$scrollProgress) return;
    const { scrollLeft = 0, scrollWidth = 0, clientWidth = 0 } = this.$carousel || {};
    this.$scrollProgress.style.width = `${((scrollLeft + clientWidth) / scrollWidth) * 100}%`;
  }
}

customElements.define("product-recommendation", ProductRecommendation);
